import StatusCode from 'status-code-enum';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';

import type { ReportAdSubmissionValues } from 'components/Toolkit/ReportAdModal/ReportAdModal';

import { SendMessageModal } from 'components/PageWrappers/AdDetails/components/SendMessageModal/SendMessageModal';
import { ReportAdModal } from 'components/Toolkit/ReportAdModal/ReportAdModal';
import { StripeCaptureBuyerPhoneNumberModal } from 'components/PageWrappers/AdDetails/components/StripeCaptureBuyerPhoneNumberModal/StripeCaptureBuyerPhoneNumberModal';
import { RevealBoostModal } from 'components/PageWrappers/SharedComponents/RevealBoostModal/RevealBoostModal';
import { RevealShareModal } from 'components/PageWrappers/SharedComponents/RevealShareModal/RevealShareModal';
import { DealerEnquiryModal } from 'components/PageWrappers/AdDetails/components/DealerEnquiryModal/DealerEnquiryModal';
import { PostEnquirySimilarAdsModal } from 'components/PageWrappers/AdDetails/components/PostEnquirySimilarAdsModal/PostEnquirySimilarAdsModal';
import { HistoryCheckModal } from 'components/Toolkit/HistoryCheckModal/HistoryCheckModal';
import { TrustedDealerModal } from 'components/SearchPage/TrustedDealerModal/TrustedDealerModal';
import { PricePerMonthModal } from 'components/PageWrappers/AdDetails/components/AdTitleBox/PricePerMonthModal/PricePerMonthModal';

import { format422ErrorsFromApiForForm } from 'helpers/forms';
import { fireToast } from 'helpers/Toasts';
import { RevealSellerAnalyticsModal } from 'components/PageWrappers/SharedComponents/RevealSellerAnalyticsModal/RevealSellerAnalyticsModal';
import { handleCallStripeCheckout } from 'helpers/handleCallStripeCheckout';
import { useAdDetailsContext } from 'contexts/AdDetailsContext';
import { adWriteApi } from 'api/adWriteApi';
import { useUserContext } from 'contexts/UserContext';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';
import { GTM_PAGES, SELLER_ANALYTICS_CATEGORIES } from 'types';

import { useAuthOptions } from 'hooks/useAuthOptions';
import { formatAuthorizationHeader } from 'helpers/auth';
import { SavedSearchModal } from 'components/PageWrappers/SharedComponents/SavedSearchModal/SavedSearchModal';
import { useSavedSearchModal } from 'components/PageWrappers/SharedComponents/SavedSearchModal/SavedSearchModal.hook';
import { isOk } from 'domains/Result';
import { orderRepository } from 'repositories/Order/OrderRepository';

const ReservationModal = dynamic(
  import('../ReservationModal/ReservationModal').then(
    (mod) => mod.ReservationModal,
  ),
);

import type {
  DealerSummary,
  FinanceInfo,
  Reservation,
} from 'components/PageWrappers/AdDetails/types';
import { rg4js } from 'helpers/raygun';
import { PAGE } from 'helpers/pages';
import { DISPLAY_RESERVATION } from '../../../../../utils';

type verification = {
  email?: boolean;
  phone?: boolean;
  bank?: boolean;
};

type SavedSearchData = {
  name: string;
  terms: Array<string>;
};

export interface AdDetailsComponentsThatAreNotInitiallyVisibleProps {
  baseUrl: string;
  cdnUrl: string;
  adId: number;
  title: string;
  sectionName?: string;
  illustration: string;
  county?: string;
  type?: string;
  isTrader: boolean;
  verification?: verification;
  friendlyUrl?: string;
  dealer?: DealerSummary;
  sellerName: string;
  adTitle: string;
  refreshTopAndSideDFPUnit: () => void;
  savedSearchData?: SavedSearchData;
  relativeUrl: string;
  financeInfo: FinanceInfo | null;
  reservation?: Reservation;
}

const AdDetailsComponentsThatAreNotInitiallyVisible = (
  props: AdDetailsComponentsThatAreNotInitiallyVisibleProps,
) => {
  const {
    baseUrl,
    cdnUrl,
    adId,
    title,
    sectionName,
    illustration,
    county,
    type,
    isTrader,
    verification,
    friendlyUrl,
    dealer,
    sellerName,
    adTitle,
    refreshTopAndSideDFPUnit,
    savedSearchData,
    relativeUrl,
    financeInfo,
    reservation,
  } = props;

  const {
    isReportAdModalOpen,
    setIsReportAdModalOpen,
    currentReportAdView,
    setCurrentReportAdView,
    categories,
    isSendMessageModalOpen,
    setIsSendMessageModalOpen,
    isShareModalOpen,
    setIsShareModalOpen,
    message,
    setIsPostEnquirySimilarAdsModalOpen,
    setSimilarAds,
    isPostEnquirySimilarAdsModalOpen,
    isStripeModalOpen,
    setIsStripeModalOpen,
    similarAds,
    isDealerEnquiryModalOpen,
    enquiry,
    isPricePerMonthModalOpen,
    setIsPricePerMonthModalOpen,
    isTrustedDealerModalOpen,
    setTrustedDealerModalOpen,
  } = useAdDetailsContext();

  const router = useRouter();

  const {
    user,
    isBoostModalOpen,
    setIsBoostModalOpen,
    boostModalData,
    isHistoryCheckModalOpen,
    setIsHistoryCheckModalOpen,
    isSellerAnalyticsModalOpen,
    setIsSellerAnalyticsModalOpen,
    setSellerAnalyticsModalDisplay,
    sellerAnalyticsModalDisplay,
    isSavedSearchModalOpen,
    isInstantAlertAvailable,
    setIsSavedSearchModalOpen,
    selectedNotificationFrequency,
    setSelectedNotificationFrequency,
  } = useUserContext();
  const { stripeQuantity } = useAdDetailsContext();
  const { accessToken } = useAuthOptions();

  // Report Ad

  async function onReportAd({
    name,
    email,
    reason,
    category,
    consent,
  }: ReportAdSubmissionValues) {
    try {
      const reportAdPayload = {
        consent,
        name,
        email,
        reason,
        category,
        ad: adId,
      };

      const { status } = await adWriteApi.reportAd(
        reportAdPayload,
        formatAuthorizationHeader(accessToken),
      );
      if (status === StatusCode.SuccessOK) {
        setCurrentReportAdView('SUCCESS');
      }
    } catch (error) {
      rg4js('send', {
        error,
        customData: {
          pageName: PAGE.AD_DETAILS,
          functionName: 'onReportAd',
        },
      });
      if (
        error?.response?.status === StatusCode.ClientErrorUnprocessableEntity
      ) {
        return format422ErrorsFromApiForForm(error.response.data);
      } else {
        fireToast({
          type: 'ERROR',
          text: 'Sorry we couldn’t report this ad right now, please try again later',
        });
      }
      setIsReportAdModalOpen(false);
    }
  }

  function onClose() {
    setCurrentReportAdView('INITIAL');
  }

  // Boost

  const handleSelectBoost = async (bundleID: number) => {
    const productIds: number[] = [bundleID];

    const data = await orderRepository.upsell(
      { adId, productIds },
      accessToken ?? '',
    );
    if (isOk(data)) {
      const orderID = data.ok.id;
      router.push(`/order/${orderID}`);
    } else {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
    }
    setIsBoostModalOpen(false);
  };

  const { handleOnSubmitSave, saving, setSavedSearchName, savedSearchName } =
    useSavedSearchModal({
      adId,
      searchName: savedSearchData?.name,
      searchTerms: savedSearchData?.terms,
    });

  // Send Enquiry and Send Message

  useOnUpdateOnly(() => {
    setIsPostEnquirySimilarAdsModalOpen(false);
  }, [adId]);

  return (
    <>
      {verification && !dealer && isSendMessageModalOpen && (
        <SendMessageModal
          isOpen={isSendMessageModalOpen}
          askToClose={() => setIsSendMessageModalOpen(false)}
          title={title}
          illustration={illustration}
          county={county}
          type={type}
          isTrader={isTrader}
          verification={verification}
          message={message}
          sendButtonTrackingId="contact-seller-send-btn"
          adId={adId}
          sectionName={sectionName}
          authorizationHeader={formatAuthorizationHeader(accessToken)}
          setIsPostEnquirySimilarAdsModalOpen={
            setIsPostEnquirySimilarAdsModalOpen
          }
          setSimilarAds={setSimilarAds}
        />
      )}
      {categories && (
        <ReportAdModal
          flagImageUrl={`${cdnUrl}/images/illustrations/report-ad.svg`}
          categories={categories}
          isOpen={isReportAdModalOpen}
          askToClose={() => setIsReportAdModalOpen(false)}
          onReportAd={onReportAd}
          onClose={onClose}
          view={currentReportAdView}
          isUserLoggedIn={Boolean(user)}
        />
      )}
      <RevealShareModal
        isOpen={isShareModalOpen}
        askToClose={() => setIsShareModalOpen(false)}
        friendlyUrl={friendlyUrl}
      />
      {isPostEnquirySimilarAdsModalOpen && (
        <PostEnquirySimilarAdsModal
          isOpen={isPostEnquirySimilarAdsModalOpen}
          askToClose={() => {
            refreshTopAndSideDFPUnit();
            setIsPostEnquirySimilarAdsModalOpen(false);
          }}
          domain={baseUrl}
          vendorName={dealer?.name ?? sellerName}
          adId={adId}
          authorisationHeader={formatAuthorizationHeader(accessToken)}
          similarAds={similarAds}
        />
      )}
      {boostModalData?.boostInfoList && (
        <RevealBoostModal
          isOpen={isBoostModalOpen}
          askToClose={() => setIsBoostModalOpen(false)}
          cdnUrl={cdnUrl}
          baseUrl={baseUrl}
          adId={adId}
          boostInfoList={boostModalData.boostInfoList}
          pageType={GTM_PAGES.AD_DETAILS}
          spotlightEligible={boostModalData.spotlightEligible}
          bumpEligible={boostModalData.bumpEligible}
          section={boostModalData.section}
          handleSelect={handleSelectBoost}
        />
      )}
      {isHistoryCheckModalOpen && (
        <HistoryCheckModal
          isOpen={isHistoryCheckModalOpen}
          askToClose={() => setIsHistoryCheckModalOpen(false)}
          adId={adId}
          title={adTitle}
          dataTrackingValue="purchase-history-check"
          relativeUrl={relativeUrl || ''}
        />
      )}
      <StripeCaptureBuyerPhoneNumberModal
        adId={adId}
        isOpen={isStripeModalOpen}
        askToClose={() => setIsStripeModalOpen(false)}
        cdnUrl={cdnUrl}
        stripeQuantity={stripeQuantity}
        handleCallStripeCheckout={handleCallStripeCheckout}
      />
      {user && sellerAnalyticsModalDisplay?.data && (
        <RevealSellerAnalyticsModal
          cdnUrl={cdnUrl}
          baseUrl={baseUrl}
          askToClose={() => setIsSellerAnalyticsModalOpen(false)}
          isOpen={isSellerAnalyticsModalOpen}
          showCloseIcon
          title={sellerAnalyticsModalDisplay.title}
          selectedTab={SELLER_ANALYTICS_CATEGORIES.AD_VIEWS}
          adId={adId}
          userId={user?.id}
          setSellerAnalyticsData={setSellerAnalyticsModalDisplay}
          sellerAnalyticsData={sellerAnalyticsModalDisplay.data}
        />
      )}
      {savedSearchData && (
        <SavedSearchModal
          isOpen={isSavedSearchModalOpen}
          saving={saving}
          name={savedSearchName}
          terms={savedSearchData?.terms}
          askToClose={() => setIsSavedSearchModalOpen(false)}
          onSave={handleOnSubmitSave}
          isInstantAlertAvailable={isInstantAlertAvailable}
          setName={setSavedSearchName}
          selected={selectedNotificationFrequency}
          setSelected={setSelectedNotificationFrequency}
        />
      )}
      {dealer && isDealerEnquiryModalOpen && (
        <DealerEnquiryModal
          isOpen
          askToClose={router.back}
          isUserLoggedIn={Boolean(user)}
          dealerName={dealer.name}
          dealerLogo={dealer?.logo?.small}
          enquiry={enquiry}
          adId={adId}
          sectionName={sectionName}
          authorizationHeader={formatAuthorizationHeader(accessToken)}
          setIsPostEnquirySimilarAdsModalOpen={
            setIsPostEnquirySimilarAdsModalOpen
          }
          setSimilarAds={setSimilarAds}
        />
      )}
      {isPricePerMonthModalOpen && financeInfo && (
        <PricePerMonthModal
          isOpen={isPricePerMonthModalOpen}
          close={() => setIsPricePerMonthModalOpen(false)}
          financeInfo={financeInfo}
        />
      )}
      {isTrustedDealerModalOpen && (
        <TrustedDealerModal
          isOpen={isTrustedDealerModalOpen}
          askToClose={() => setTrustedDealerModalOpen(false)}
        />
      )}
      {DISPLAY_RESERVATION && reservation && (
        <ReservationModal
          value={reservation.value}
          currency={reservation.currency}
          adId={adId}
        />
      )}
    </>
  );
};

export { AdDetailsComponentsThatAreNotInitiallyVisible };
